// @flow

import {
  NORMAL_EDIT_MODE,
  UNKNOWN_EDIT_MODE,
} from 'app/beta-editor/constants/EditModes';

import type { Plan } from '@zapier/design-system-revenue';
import type { TrialExtensionReason } from '@zapier/billing';

import type { DataMap, DateableString, ID } from 'app/common/types';
import type { Field, Fields, Give } from 'app/beta-editor/types';
import type { Service, ServicesMap } from 'app/service/types';
import type { TemplateStatus } from 'app/developer/components/ZapTemplates/constants';

// The type of a action node (ie any step but a trigger).
export type FlowNodeWriteType =
  | 'write'
  | 'search'
  | 'search_or_write'
  | 'filter';

// The type of a node in a zap:
//   'read' - a trigger step
//   'write' - an action step
//   'search' - a  find step
//   'search_or_write' - a find or create step
//   'filter' - a filter step
export type FlowNodeType = 'read' | FlowNodeWriteType;

type ValidationFlags = {|
  is_opened: boolean,
  is_edited?: boolean,
  is_valid?: boolean,
|};

export type ParamMap = { [fieldName: string]: any };

export type NodeMetaTitles = {
  stepTitle?: string,
};

export type NodeMeta = {
  // Fields the user has filled in on a node (aka 'step').
  // Values can be of various types - strings, numbers,
  // objects, arrays of objects...
  parammap: ParamMap,

  // Fields chosen in a previous step. The keys
  // contain the encoded node and id field info,
  // ie '1234__date', and the values are the labels.
  selectedGives: { [key: string]: string },

  validation: {
    action?: ValidationFlags,
    params?: {
      ...ValidationFlags,
      invalid_mappings: {
        // key is the field name, value is a string with the node id
        // and field key, ie '1234__date'
        [fieldname: string]: Array<string>,
      },
    },
    webhook?: ValidationFlags,
  },

  // Used for renaming steps in the editor.
  ...$Exact<NodeMetaTitles>,
  edit_mode: ?string,
};

// holds Path attributes when available
export type NodeParams = {
  name?: string,
  color?: string,
  emoji?: string,
  [fieldname: string]: string | {},
};

export type NodePlanLimits = {
  plans: {
    monthly: Plan,
    annual: Plan,
  },
  reason: TrialExtensionReason,
};

// The very basic properties of a node, used in both the editor
// and task history.
export type SkinnyNode = {
  id: ID,

  // The previous node / step in the zap. For the first step this will
  // be null.
  parent_id?: ID,

  // Confusingly, the task history api returns nodes with a parent prop, not parent_id! :o
  parent?: ID,

  // The type of this node - read (trigger), write (action), etc.
  type_of: FlowNodeType,

  // The key of the service or app for this node. For example, 'TrelloAPI'.
  selected_api: string,

  // TODO: what is this?
  action: string,
};

// One node (ie one step) in a zap.
export type FlowNode = {
  ...$Exact<SkinnyNode>,

  // The step name provided by the user
  title?: string,

  authentication_id?: number,

  // The id of the zap that this node is associated with.
  // The zap id and the id of the trigger step (first step)
  // are the same.
  root_id?: number,

  // Is the root node paused?
  root_paused?: boolean,

  // field values filled in by user
  params: NodeParams,

  // meta information about this node supplied by the backend
  meta: NodeMeta,

  // ids of children nodes of this node
  children_ids: Array<ID>,

  // Marked whether a node was created in Zap Template Mode. All nodes under
  // such a created Zap will have this set to `true`.
  is_template?: boolean,

  isPremium?: boolean,
  is_premium?: boolean,

  // web hook subscription url
  subscription_url?: string,

  // is this node paused?
  paused?: boolean,

  // any plan limits that would prevent the parent node being turned on
  plan_limits?: NodePlanLimits,

  // is the title being edited?
  isEditingStepTitle?: boolean,

  polling_interval_override?: number,

  // this will be present for Subscription Hook nodes
  code?: string,

  // This is used in NodeUtils.staticHookIdentifier. I think it must have been
  // where we started putting an old version of the code when the flow object was
  // changed at some point, but it's hard to be sure. I think this was used before
  // we had the flow object. If we get rid of that use in it in NodeUtils at some point,
  // we can remove this.
  legacy?: {
    code?: string,
  },
};

// Controls how much the user can edit the zap. For guided zaps it is
// very restricted.
export type EditMode = NORMAL_EDIT_MODE | UNKNOWN_EDIT_MODE;

// The result of the last test (aka 'test this step') of a step.
export type TestStatus =
  | 'data'
  | 'error'
  | 'halted'
  | 'nodata'
  | 'out-of-sync'
  | 'stale'
  | 'success'
  | 'unknown'
  | 'untried'
  | 'waiting';

// The state of a zap, either on, off, or in draft mode (ie incomplete).
export type ZapState = 'on' | 'off' | 'draft';

// One step in a zap. All of the data here is completely
// denormalized could instead be derived from the associated
// node. We could create the appropriate selectors and get
// rid of this.
export type FlowStep = {
  action: string,

  // TODO: are we still using this?
  isBegin: boolean,

  // TODO: are we still using this?
  isEnd: boolean,

  // Always true I think. In the past filters steps were not
  // real 'nodes', but now they are.
  isNode: boolean,

  nodeId: ID,

  // The key of the service or app for this step. For example, 'TrelloAPI'.
  selected_api: string,

  // The type of this step, ie 'read' (trigger), 'write' (action), etc.
  type: FlowNodeType,

  // Seems like this is a duplicate key for the same type above, though
  // sometimes it doesn't exist
  type_of?: FlowNodeType,

  authentication_id?: number,

  params?: {},
};

// One choice value in a dropdown.
type ReplaceChoice = {
  // the actual value to send to the server (not the label)
  value: string,

  // TODO...
};

// One field in an action or a trigger, as defined in the dev app.
export type Need = {
  // Is this a custom / dynamic field?
  custom_field: boolean,

  // The default value for this field if the user doesn't fill anything in
  default: string,

  // The help text to display along side the field in the editor
  help_text: string,

  // HTML version of the help test. Use this instead of help_text if present.
  help_text_html?: string,

  // Is this an important field? Important fields are always visible, without
  // having to click 'view more' or such.
  important: boolean,

  // Is this a field a nested, accordion style drop-down?
  // This is *not* provided by the dev app, but is instead derived by
  // the frontend based on the kind of field. See FieldUtils#injectReplaceChoices.
  isAccordion: boolean,

  // The unique key for this field.
  key: string,

  // TODO what is this?
  language?: string,

  // Is this a required field?
  required: boolean,

  // The type of the field, ie 'unicode', 'dict', etc.
  // TODO: provide an enumeration of all the valid field types,
  // instead of just using string.
  type: string,

  // TODO: what's the difference between type and zapierType?
  zapierType: string,

  // For dropdown fields, the list of available choices
  replaceChoices: ReplaceChoice[],
};

// The TaskState from testing a Flow.
export type FlowTaskState = {
  breadcrumbs: Array<{
    status: string,
    object_type: string,
    start_time: string,
    object_id: ID,
    step: string,
    end_time: string,
    error?: ?string,
    error_bundle?: {
      id: string,
      title: string,
      code: ?string,
      status?: ?number,
    },
  }>,
  customer_id: ID,
  force_hold: boolean,
  fork: string,
  input: { [key: ID]: DataMap },
  mode: string,
  output: { [ID]: {} },
  selected_sample: ?number,
  start_time: string,
  statuses: { [ID]: TestStatus },
  step: string,
  stop: boolean,
  why_stop: {
    rule_ids: number[],
  },
  zap: { [ID]: FlowNode },
};

// Flow (Zap-Template) Finalize Form Fields
export type PublishFormValues = {
  description: ?string,
  title: ?string,
  status: TemplateStatus | '',
};
export type PublishFormMessage = { text: string, copyId?: ID };
export type PublishForm = {
  title?: '',
  fields: Object[],
  value: PublishFormValues,
  isWorking: ?boolean,
  errors?: {},
  messages?: PublishFormMessage,
};

export type FlowOwner = {
  email: string,
  id: string,
  code: string,
};

export type NodeMap = { [ID]: FlowNode };

export type FlowNeeds = { [nodeId: ID]: Field[] };

export type StaleSamples = { [nodeId: ID]: {} };

export type DirtyTaskState = { [nodeId: ID]: {} };

export type NodeLoading = {
  [ID]: number,
};

export type LastTestStatuses = { [nodeId: ID]: TestStatus };

export type FlowSteps = Array<FlowStep>;
// A Zap, created by a user in the editor.

// TODO - what's in here?
export type FlowNeedsMeta = {};

export type Flow = {
  // Persistent server id of this zap.
  id: ID,

  // Temporary client id, for zaps that have not been saved yet.
  cid: ID,

  // Is this zap fully loaded? We keep a skinny list of all of the user's zaps
  // around to show on the dashboard, but only fully load the zap currently being
  // edited. When the user starts editing a different zap we keep the prior zap
  // around but strip out most of it's properties, to save memory.
  isLoaded: true,

  isLoading?: boolean,

  // The user who created this zap.
  account_id: number,

  folder_ids: ID[],

  dirtyNeeds: { ID?: boolean },

  // TODO: Filled in by server, but doesn't look like we're using this?
  has_shared: boolean,

  // Is this a shared zap?
  is_shared: boolean,

  isLoadingNeeds: boolean,

  // are we loading all the needs for the zap?
  isLoadingAllNeeds?: boolean,

  isToggling: boolean,

  // Can the user edit fully, or is this a guided zap?
  edit_mode: EditMode,

  givesAll: { ID?: Array<Give> },

  // Edit mode is retained until returning to the dashboard.
  temporaryEditMode?: EditMode,

  // The last time this zap ran
  last_live_at: ?DateableString,

  // The last time this zap was paused
  last_paused_at: ?DateableString,

  // The last time this zap was changed
  lastchanged: ?DateableString,

  // The nodes (aka 'steps') of this zap, keyed by the
  // node id. Node ids are *not* in step order (see node.parent_id).
  nodes: NodeMap,

  // The list of node ids of this zap, ordered by as they
  // appear in the zap (also see steps below).
  nodeIds: ID[],

  nodeSkipping: { [ID]: FlowNode },

  // The profile id of the owner of this zap.
  ownerId?: number,

  // Same as above, depending on casing status.
  owner_id?: number,

  // More metadata about the owner of this zap.
  owner: FlowOwner,

  // Is this zap paused?
  paused: boolean,

  // Was it paused because the user went over their plan limits?
  paused_by_plan: boolean,

  progress: number[],

  // Is this zap on, off, or in draft mode (ie not complete)?
  state: ZapState,

  // TODO: what is this?
  tasks: number,

  // the timezone used by the Zap
  timezone?: string,

  // The zap title provided by the user
  title: string,

  sampleStates: { [key: string]: boolean },

  // The results of the last n (currently 3) tests of this zap
  lastTestStatuses: LastTestStatuses,

  // List of denormalized info about each step in this zap, in step order.
  steps: FlowSteps,

  // The field definitions for each node in this zap. An array of Field
  // objects, keyed by node id.
  needs: FlowNeeds,

  // Meta info provided by the server about the needs.
  needsMeta: FlowNeedsMeta,

  // Indexes of open step menus within flow.
  openStepMenus: { [stepIndex: number]: boolean },

  // Sample data from the app given a node's selected_api and action.
  appSample?: {},

  // Lots and lots of info about the tests.
  taskState: FlowTaskState,

  // TODO
  pages: {},

  // Last updated timestamps for nodes in this zap.
  nodeUpdateStamps: { [nodeId: ID]: number },

  // Zap removal status:
  //   'initial'   - initial state, haven't tried to remove yet
  //   'removing'  - it's being removed
  //   'finished'  - we've finished removing it (successfully or unsuccessfully)
  removalStatus?: 'initial' | 'removing' | 'finished',

  // Fields that couldn't be loaded by the server.
  missingNeeds: { ID: boolean | Field },

  // Params are on this node, but there are no such fields.
  extraNeeds: { ID: ?Array<Field> | Array<*> | Field },

  // is this a new, un-saved zap?
  isNew?: boolean,

  // Map of node ids to numbers, where the number is incremented everytime
  // we're still waiting for a node. Zero means we're not waiting for the node.
  nodeLoading: NodeLoading,

  // Flow note that might includes some description of the Zap.
  note?: string,

  // which add step picker is open?
  openAddStepPickerNodeId: ?ID,

  // are we currently adding or removing a step?
  isAddingOrRemovingStep: boolean,

  // info used to publish a zap template
  publish: PublishForm,

  // are gives being loaded for a given node?
  givesLoading: { [nodeId: ID]: boolean },

  dirtyTaskState: DirtyTaskState,

  staleSamples: StaleSamples,
};

// A flow object that has been gutted to save memory.
// It still has the basic info the dashboard needs.
type SkinnyFlow = {
  id: ID,
  timezone?: string,
  title?: string,
  isLoaded: false,
  nodeIds: ID[],

  // Only contains scalar properties of each node,
  // ie strings, numbers. No array or object properties.
  nodes: {},
};

// A regular Flow object OR a SkinnyFlow object. This is a
// disjoint union type: https://flow.org/en/docs/types/unions/#toc-disjoint-unions.
// The discriminator property is 'isLoaded': if isLoaded is true, flow
// knows that it is a Flow object. If false, flow knows that it is a SkinnyFlow.
export type MaybeSkinnyFlow = Flow | SkinnyFlow;

// An issue with one of the apps (services) used in a zap.
export type Incident = {
  // Status of the incident - 'resolved', etc. TODO what are the others?
  status: string,

  // The service with the issue.
  service: {
    // NOT like Service
    id: number, // NOT a string like Service
    key: string,
    slug: string,
    title: string,
    is_healthy: boolean,
    status: string,
  },
  statuspageio_id: ?ID,
  name: string,
  message: string,
  updated_at: string,
  id: number,
};

// The flow reducer state object.
export type FlowState = {
  // List of loaded flows (zaps), keyed by flow id.
  // Could be a mix of fully loaded Flow objects
  // and gutted SkinnyFlow objects.
  flows: { [flowId: ID]: MaybeSkinnyFlow },

  // IDs of flow objects currently open in the editor.
  // Max of just one for now.
  openFlowIds: ID[],

  didScrollErrors: boolean,

  // List of incidents for services used in user's zaps.
  incidents: Incident[],

  // The next client id to use for a new zap
  nextCid: number,

  // List of least-recently-used flow ids, to determine
  // which flow objects to evict to safe memory.
  lru: ID[],

  copiedZaps?: {},
};

export type NodePredicate = FlowNode => boolean;

export type FlowProps = {| flow: Flow |};

// TODO use this in the `query` field in `FlowContainerTypes`
export type FlowQuery = {
  is_new?: boolean,
  create?: boolean,
  referrer?: string,
  simple?: string,
  from_nouns?: string,
};

export type ComponentProps = FlowProps & {|
  nodeId: ID,
  isZapTemplate: boolean,
  node: FlowNode,
  service: Service,
  services: ServicesMap,
|};

// This is almost certainly incomplete
export type FieldsPageContainerProps = {
  flow: Flow,
  flowId: ID,
  nodeId: ID,
  node: FlowNode,
  services: ServicesMap,
  fields: Fields,
  query?: FlowQuery,
  shouldUseSimpleZapSetup: boolean,
  // This will be removed by https://zapierorg.atlassian.net/browse/PDOT-1368
  personalizationExperiments?: ?{
    shouldHideContinueButton?: boolean,
    shouldHideOptionalFields?: boolean,
  },
};

export type Meta = {
  flow: Flow,
  node: FlowNode,
  service: Service,
};

export const ZAP_ON = 'on';
export const ZAP_OFF = 'off';
export const ZAP_ERROR = 'error';
export const ZAP_SAVING = 'saving';

export type ZapStatus = 'off' | 'on' | 'error' | 'saving';

export type NodesSampleData = {
  taskState: FlowTaskState,
  dirtyTaskState: DirtyTaskState,
  nodeLoading: NodeLoading,
  staleSamples: StaleSamples,
};
